import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createAttestation } from '../../services/attestationApi';
import { dealsApi } from '../../services/api';
import { Button } from '../ui';

/**
 * RequestAttestationModal — two-button CPA attestation fork.
 * Props:
 *   dealId        {string}
 *   dealnexaCpa   { available: boolean, firmName: string|null }
 *   supportingDocs  [{ id, filename }]  (auto-selected from QofE citations; checked by default)
 *   onClose       () => void
 *   onCreated     (created) => void
 */
export default function RequestAttestationModal({
  dealId,
  dealnexaCpa,
  supportingDocs = [],
  onClose,
  onCreated,
}) {
  const [choice, setChoice] = useState(null); // 'byo' | 'dealnexa'
  const [cpa, setCpa] = useState({ name: '', firm: '', email: '' });
  const [dealDocs, setDealDocs] = useState([]);     // [{ id, filename }] fetched
  const [docsLoading, setDocsLoading] = useState(true);
  const [checkedIds, setCheckedIds] = useState(null); // Set<id> | null until initialized
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const mountedRef = useRef(true);

  // Set of auto-selected doc ids (from supportingDocs prop).
  const autoIds = useMemo(
    () => new Set(supportingDocs.map((d) => d.id)),
    [supportingDocs]
  );

  // Displayed list = union of fetched deal docs and supportingDocs, de-duped by id.
  const docList = useMemo(() => {
    const byId = new Map();
    for (const d of dealDocs) {
      if (d && d.id != null) byId.set(d.id, { id: d.id, filename: d.filename, auto: autoIds.has(d.id) });
    }
    for (const d of supportingDocs) {
      if (d && d.id != null && !byId.has(d.id)) byId.set(d.id, { id: d.id, filename: d.filename, auto: true });
    }
    return Array.from(byId.values());
  }, [dealDocs, supportingDocs, autoIds]);

  // Fetch the deal's full document list on mount.
  useEffect(() => {
    mountedRef.current = true;
    setDocsLoading(true);
    dealsApi.getDocuments(dealId)
      .then((r) => {
        if (!mountedRef.current) return;
        const docs = Array.isArray(r.data) ? r.data : [];
        setDealDocs(docs.map((d) => ({ id: d.id, filename: d.filename })));
      })
      .catch(() => {
        // Fall back to just the supportingDocs prop — don't block the modal.
        if (mountedRef.current) setDealDocs([]);
      })
      .finally(() => { if (mountedRef.current) setDocsLoading(false); });
    return () => { mountedRef.current = false; };
  }, [dealId]);

  // Initialize checked state once docs resolve: auto docs checked, others unchecked.
  useEffect(() => {
    if (docsLoading || checkedIds !== null) return;
    const init = new Set();
    for (const d of docList) {
      if (d.auto) init.add(d.id);
    }
    setCheckedIds(init);
  }, [docsLoading, checkedIds, docList]);

  // Focus the dialog on mount for a11y.
  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
  }, []);

  // Escape key closes the modal.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const checked = checkedIds || new Set();
  const includedCount = docList.filter((d) => checked.has(d.id)).length;

  const toggleDoc = (id) =>
    setCheckedIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async (path) => {
    setSubmitting(true);
    setError('');
    try {
      // excludeDocIds = auto docs now unchecked; extraDocIds = non-auto docs now checked.
      const excludeDocIds = docList
        .filter((d) => d.auto && !checked.has(d.id))
        .map((d) => d.id);
      const extraDocIds = docList
        .filter((d) => !d.auto && checked.has(d.id))
        .map((d) => d.id);
      const payload =
        path === 'byo'
          ? { path, cpa, extraDocIds, excludeDocIds }
          : { path, extraDocIds, excludeDocIds };
      const created = await createAttestation(dealId, payload);
      onCreated(created);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send attestation request');
      setSubmitting(false);
    }
  };

  const byoDisabled = submitting || (choice === 'byo' && !cpa.email.trim());
  const dnxAvailable = !!(dealnexaCpa && dealnexaCpa.available);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Request CPA Attestation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,24,39,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          width: 560,
          maxWidth: '92vw',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '24px 28px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
              Request CPA Attestation
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
              Freeze this QofE and send it to a CPA for independent attestation.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9CA3AF',
              fontSize: 18,
              lineHeight: 1,
              padding: '2px 4px',
              borderRadius: 4,
              transition: 'color 0.15s ease',
              marginLeft: 16,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#111827'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>

          {/* Fork: choose path */}
          {!choice && (
            <div style={{ display: 'grid', gap: 12 }}>
              <ChoiceCard
                onClick={() => setChoice('byo')}
                enabled
                title="Invite your own CPA"
                description="Send to a CPA you already work with."
              />
              <ChoiceCard
                onClick={() => dnxAvailable && setChoice('dealnexa')}
                enabled={dnxAvailable}
                title={
                  <>
                    Use a DealNexa CPA{' '}
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#0D6EFD' }}>
                      · No cost
                    </span>
                  </>
                }
                description={
                  dnxAvailable
                    ? `Your QofE will be routed to ${dealnexaCpa.firmName}.`
                    : "Coming soon — we're finalizing our CPA partner."
                }
              />
            </div>
          )}

          {/* BYO form */}
          {choice === 'byo' && (
            <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
              <FieldInput
                placeholder="CPA name (optional)"
                value={cpa.name}
                onChange={(v) => setCpa({ ...cpa, name: v })}
              />
              <FieldInput
                placeholder="Firm name (optional)"
                value={cpa.firm}
                onChange={(v) => setCpa({ ...cpa, firm: v })}
              />
              <FieldInput
                placeholder="CPA email *"
                type="email"
                value={cpa.email}
                onChange={(v) => setCpa({ ...cpa, email: v })}
                required
              />
            </div>
          )}

          {/* DealNexa confirm */}
          {choice === 'dealnexa' && (
            <div
              style={{
                background: '#E7F1FF',
                borderRadius: 10,
                padding: '14px 16px',
                marginBottom: 20,
                fontSize: 14,
                color: '#111827',
                lineHeight: 1.6,
                border: '1px solid #BDD6FF',
              }}
            >
              Your QofE will be sent to{' '}
              <strong>{dealnexaCpa && dealnexaCpa.firmName}</strong> — included
              in your plan.
            </div>
          )}

          {/* Supporting documents list (only when a path is chosen) */}
          {choice && (
            <>
              <div
                style={{
                  marginBottom: 20,
                  background: '#F8F9FA',
                  borderRadius: 10,
                  padding: '14px 16px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginBottom: 10,
                  }}
                >
                  Supporting documents
                  {docList.length > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontWeight: 500,
                        color: '#9CA3AF',
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                    >
                      ({includedCount} of {docList.length} documents included)
                    </span>
                  )}
                </div>
                {docsLoading ? (
                  <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.5 }}>
                    Loading documents…
                  </div>
                ) : docList.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.5 }}>
                    No documents found. The backend will auto-select all documents
                    linked to this deal.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 4 }}>
                    {docList.map((d) => (
                      <label
                        key={d.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 4px',
                          fontSize: 13,
                          color: '#374151',
                          cursor: 'pointer',
                          borderRadius: 6,
                          transition: 'background 0.12s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <input
                          type="checkbox"
                          checked={checked.has(d.id)}
                          onChange={() => toggleDoc(d.id)}
                          style={{ accentColor: '#0D6EFD', width: 15, height: 15 }}
                        />
                        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.filename}
                        </span>
                        {d.auto && (
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: 10,
                              fontWeight: 600,
                              color: '#0D6EFD',
                              background: '#E7F1FF',
                              border: '1px solid #BDD6FF',
                              borderRadius: 999,
                              padding: '1px 7px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                            }}
                          >
                            auto-selected
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div
                  style={{
                    background: '#F8D7DA',
                    border: '1px solid #F1AEB5',
                    borderRadius: 8,
                    padding: '10px 14px',
                    fontSize: 13,
                    color: '#DC3545',
                    marginBottom: 16,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setChoice(null)}
                  isDisabled={submitting}
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => submit(choice)}
                  isDisabled={byoDisabled}
                  isLoading={submitting}
                  style={{ background: '#0D6EFD', color: '#FFFFFF', borderColor: '#0D6EFD' }}
                >
                  {submitting ? 'Sending…' : 'Send to CPA'}
                </Button>
              </div>
            </>
          )}

          {/* Legal watermark */}
          <div
            style={{
              marginTop: 22,
              fontSize: 11,
              color: '#9CA3AF',
              borderTop: '1px solid #E5E7EB',
              paddingTop: 14,
              lineHeight: 1.5,
            }}
          >
            DRAFT — UNATTESTED — prepared by DealNexa platform. The CPA's signed letter is the attested deliverable.
          </div>
        </div>
      </div>
    </div>
  );
}

function ChoiceCard({ onClick, enabled, title, description }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={!enabled}
      aria-disabled={!enabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        textAlign: 'left',
        padding: '16px 18px',
        borderRadius: 12,
        border: `1.5px solid ${hovered && enabled ? '#0D6EFD' : '#E5E7EB'}`,
        background: hovered && enabled ? '#F0F7FF' : '#FFFFFF',
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.6,
        transition: 'border-color 0.15s ease, background 0.15s ease',
        width: '100%',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, color: enabled ? '#111827' : '#9CA3AF', marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
        {description}
      </div>
    </button>
  );
}

function FieldInput({ placeholder, value, onChange, type = 'text', required }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '10px 14px',
        border: '1.5px solid #E5E7EB',
        borderRadius: 8,
        fontSize: 14,
        color: '#111827',
        fontFamily: 'inherit',
        outline: 'none',
        transition: 'border-color 0.15s ease',
        width: '100%',
        boxSizing: 'border-box',
      }}
      onFocus={(e) => { e.currentTarget.style.borderColor = '#0D6EFD'; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
    />
  );
}
